import React, { useMemo, useState } from 'react'
import { Button, Card, Spinner, Table } from 'react-bootstrap'
import { Building, ChevronLeft, ChevronRight, Clock, Globe } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { useCustomerRenewalDetail } from '@data/renewals/renewals'
import { IDeviceRenewalRow } from '@data/renewals/model/renewals.model'
import BillingTimelineDrawer from './BillingTimelineDrawer'
import RenewalStatusSelect from './RenewalStatusSelect'
import { useAuthStore } from '@store/auth.store'
import { hasPermissions } from '@common/utils/validation/authValidators'
import './Renewals.scss'

type RowsPerPage = 10 | 20 | 'all'

const CURRENCY_SYMBOL: Record<string, string> = {
  eur: '€',
  gbp: '£',
  usd: '$',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

function formatPrice(minor: number | null, currency: string | null): string {
  if (minor === null || !currency) return '—'
  const symbol = CURRENCY_SYMBOL[currency.toLowerCase()] ?? currency.toUpperCase() + ' '
  return `${symbol}${(minor / 100).toFixed(0)}`
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

const CustomerRenewalDetail: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>()
  const accountIdNum = accountId ? Number(accountId) : undefined
  const { data: detail, isLoading, isError } = useCustomerRenewalDetail(accountIdNum)
  const [showTimeline, setShowTimeline] = useState(false)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(10)
  const permissions = useAuthStore((state) => state.permissions)
  // Only admins may pin a migration status; everyone else sees a read-only badge.
  const canOverrideStatus = hasPermissions(permissions, ['UPDATE:SUBSCRIPTION:STATUS'])

  const totalRows = detail?.devices.length ?? 0
  const pageSize = rowsPerPage === 'all' ? Math.max(totalRows, 1) : rowsPerPage
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize))
  const safePage = Math.min(page, totalPages)
  const pageStart = (safePage - 1) * pageSize
  const pageEnd = Math.min(pageStart + pageSize, totalRows)

  const visible = useMemo(
    () => (detail?.devices ?? []).slice(pageStart, pageEnd),
    [detail, pageStart, pageEnd],
  )

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <Spinner animation="border" />
      </div>
    )
  }
  if (isError || !detail) {
    return <p className="text-danger p-3">Failed to load renewal details.</p>
  }

  const op = detail.operational_status

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Customer — Renewal Detail</h4>
        <Button variant="outline-primary" size="sm" onClick={() => setShowTimeline(true)}>
          <Clock size={14} className="me-1" /> View Timeline
        </Button>
      </div>

      <div className="sf-detail-header mb-3">
        <div className="sf-detail-header__avatar">{initials(detail.account_name)}</div>
        <div className="flex-grow-1 d-flex justify-content-between flex-wrap gap-3">
          <div>
            <div className="sf-detail-header__section-label">Customer</div>
            <div className="fw-bold fs-5">{detail.account_name}</div>
            {detail.billing_entity_name && (
              <div className="text-muted small d-inline-flex align-items-center gap-1">
                <Globe size={12} /> {detail.billing_entity_name}
              </div>
            )}
          </div>
          <div>
            <div className="sf-detail-header__section-label">Operational Status</div>
            {op.no_subscription > 0 && (
              <span className="sf-detail-header__pill sf-detail-header__pill--pending">
                No Subscription {op.no_subscription > 1 ? `(${op.no_subscription})` : ''}
              </span>
            )}
            {op.paid > 0 && (
              <span className="sf-detail-header__pill sf-detail-header__pill--paid">
                {op.paid} Paid
              </span>
            )}
            {op.open > 0 && (
              <span className="sf-detail-header__pill sf-detail-header__pill--open">
                {op.open} Open
              </span>
            )}
            {op.overdue > 0 && (
              <span className="sf-detail-header__pill sf-detail-header__pill--overdue">
                {op.overdue} Overdue
              </span>
            )}
            {op.restricted > 0 && (
              <span className="sf-detail-header__pill sf-detail-header__pill--restricted">
                {op.restricted} Restricted
              </span>
            )}
          </div>
          <div>
            <div className="sf-detail-header__section-label">Total Scale</div>
            <div className="fw-bold">
              {detail.location_count} Locations / {totalRows} Devices
            </div>
          </div>
        </div>
      </div>

      <Card>
        <Card.Body>
          <div className="sf-card-title">DEVICE BREAKDOWN</div>
          <Table responsive hover className="mb-0 align-middle">
            <thead>
              <tr className="text-muted small">
                <th>DEVICE ID</th>
                <th>LOCATION</th>
                <th>BILLING ENTITY</th>
                <th>MONITORING PRICE</th>
                <th>RENEWAL DATE</th>
                <th>RENEWAL STATUS</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-3">
                    No devices
                  </td>
                </tr>
              ) : (
                visible.map((d) => {
                  return (
                    <tr key={d.device_account_link_id}>
                      <td className="fw-semibold">
                        {d.device_name ?? `#${d.device_account_link_id}`}
                      </td>
                      <td>
                        <span className="d-inline-flex align-items-center gap-1">
                          <Building size={12} className="text-muted" />
                          {d.location_name}
                        </span>
                      </td>
                      <td>
                        <span className="d-inline-flex align-items-center gap-1">
                          <Globe size={12} className="text-muted" />
                          {d.billing_entity_name ?? '—'}
                        </span>
                      </td>
                      <td className="fw-semibold">
                        {formatPrice(d.monitoring_price_minor, d.currency)}
                      </td>
                      <td>{formatDate(d.renewal_date)}</td>
                      <td>
                        <RenewalStatusSelect
                          accountId={Number(accountId)}
                          deviceAccountLinkId={d.device_account_link_id}
                          paymentState={d.payment_state}
                          overrideActive={d.override_active}
                          overrideReason={d.override_reason}
                          stripeDerivedStatus={d.stripe_derived_status}
                          canEdit={canOverrideStatus}
                        />
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small fw-semibold">ROWS PER PAGE:</span>
              {([10, 20, 'all'] as RowsPerPage[]).map((opt) => (
                <span
                  key={String(opt)}
                  className={`sf-window-pill ${rowsPerPage === opt ? 'active' : ''}`}
                  onClick={() => {
                    setRowsPerPage(opt)
                    setPage(1)
                  }}
                >
                  {opt === 'all' ? 'All' : opt}
                </span>
              ))}
              <span className="text-muted small ms-2">
                Showing {totalRows === 0 ? 0 : pageStart + 1}–{pageEnd} of {totalRows} devices
              </span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <Button size="sm" variant="outline-secondary" disabled={safePage === 1} onClick={() => setPage(1)}>First</Button>
              <Button size="sm" variant="outline-secondary" disabled={safePage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft size={14} />
              </Button>
              <span className="text-muted small mx-2">
                {safePage} / {totalPages}
              </span>
              <Button size="sm" variant="outline-secondary" disabled={safePage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <ChevronRight size={14} />
              </Button>
              <Button size="sm" variant="outline-secondary" disabled={safePage === totalPages} onClick={() => setPage(totalPages)}>Last</Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <BillingTimelineDrawer
        accountId={accountIdNum}
        show={showTimeline}
        onHide={() => setShowTimeline(false)}
      />
    </div>
  )
}

export default CustomerRenewalDetail
